'use client';

import { Box, SimpleGrid, Stack } from '@chakra-ui/react';
import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { IRedux } from '@/_store/types';
import { useQuery } from '@tanstack/react-query';
import productService from '@/_service/product.service';
import Details from './Details';
import Combo from './Combo';
import Slider from './Slider';
import { getSource } from '@/_utils/platform';
import type { IProduct } from '@/entities/product/model/types';
import { FEATURED_BRANDS } from '@/_utils/Constants';
import ProductDetailsSkeleton from './ProductDetailsSkeleton';

const ProductDesktop = ({
  slug,
  onSuccess,
  shipperId,
  isModal,
  initialProduct,
}: {
  slug: string;
  onSuccess?: () => void;
  shipperId: string;
  isModal?: boolean;
  initialProduct?: IProduct | null;
}) => {
  const { user, branches, session, tokens } = useSelector(
    (state: IRedux) => ({
      user: state.auth.user,
      tokens: state.auth.tokens,
      branches: state.common.branches,
      session: state.session,
    }),
    shallowEqual,
  );
  const { is_hall, order_type } = session;

  const branch = branches?.[shipperId];
  const selectedMenuId = branch?.menu_id || '';

  // Server prefetch runs without menu_id; only seed initialData before branch menu is known.
  const shouldUseInitialData = Boolean(initialProduct) && !selectedMenuId;

  const { data, isPending, isFetching, isError } = useQuery({
    queryKey: ['product-by-id', slug, shipperId, selectedMenuId],
    queryFn: () => {
      // Brand pages must use the brand-scoped client_id (mika token), not global user.id.
      const brandClientId = tokens?.[shipperId]?.client_id;
      const clientId =
        brandClientId ||
        (shipperId === FEATURED_BRANDS.yaponamama ? user?.id || '' : '');

      return productService
        .getById(
          slug,
          {
            order_source: getSource({ is_hall }),
            only_delivery: String(order_type?.type === 'delivery'),
            only_self_pickup: String(order_type?.type === 'self-pickup'),
            branch_id: branch?.id || '',
            menu_id: selectedMenuId,
            client_id: clientId,
          },
          shipperId,
        )
        .then((res) => res?.data);
    },
    enabled: Boolean(slug && shipperId),
    initialData: shouldUseInitialData ? (initialProduct ?? undefined) : undefined,
    placeholderData: initialProduct ?? undefined,
  });

  const showDetailsSkeleton = !data && !isError && (isPending || isFetching);

  return (
    <SimpleGrid
      h='full'
      color='text'
      rounded={24}
      bg='background.card'
      gridTemplateColumns='500px 1fr'
    >
      <Box p={6}>
        <Slider list={[data?.image, ...(data?.gallery || [])]} />
      </Box>
      <Stack
        gap={0}
        position='relative'
        borderLeft='1px solid'
        borderColor='#00000014'
      >
        {showDetailsSkeleton ? (
          <ProductDetailsSkeleton />
        ) : data?.type === 'combo' ? (
          <Combo data={data} onSuccess={onSuccess} isModal={isModal} />
        ) : (
          <Details data={data} onSuccess={onSuccess} isModal={isModal} />
        )}
      </Stack>
    </SimpleGrid>
  );
};

export default ProductDesktop;
